/**
 * Centralized tooltip definitions for offer fields
 * Used across AddEditOffer and ViewOffer components
 */

export const OFFER_FIELD_TOOLTIPS = {
  // Basic Information
  offer_name: 'Internal name for this offer. This is displayed to users unless overwritten.',
  scope:
    "Select the platform for this offer. Use 'emailmarketer' for email campaigns or 'reward' for internal rewards.",
  offer_category: 'Choose if this offer sells in-game currency, premium account, premium item, or bundle.',
  duration:
    'Duration the item is valid. Common: 1 Day=86400, 1 Week=604800, 1 Month=2678400, 6 Months=16070400, 1 Year=31536000',
  quantity: 'Set to >=1 if this offer has limited purchase quantity. Default 0 for unlimited.',
  price: 'The price in the selected currency (e.g., 9.99). Use a DOT as decimal separator.',
  comment: 'Internal notes about this offer. Not visible to users.',

  // Availability & Targeting
  start_date: 'When this offer becomes available in the shop.',
  end_date: 'When this offer disappears from the shop.',
  country: 'Limit offer to specific countries. Leave empty for all countries. Offers auto-select based on currency.',
  min_user_age: 'Minimum user age in seconds for displaying the offer. Set to 0 for no minimum.',
  max_user_age: 'Maximum user age in seconds for displaying the offer. Set to 0 for no maximum.',
  min_ltv:
    "Minimum lifetime value (based on user's ebpSum). Set to 0 for no minimum. If the user's LTV is below this value, the offer will not be displayed.",
  max_ltv:
    "Maximum lifetime value allowed (based on user's ebpSum). Set to 0 for no maximum. If the user's LTV exceeds this value, the offer will not be displayed.",
  user_id:
    'Set your userID to preview this offer. Only visible to this user until set to empty. Supports comma-separated IDs.',

  // Display & Customization
  desktop_background: 'Large main image for featured offers at the top of the shop.',
  mobile_background: 'A main image for featured offers at the top of the shop in mobile view.',
  popup_background: 'Background image for the offer popup shown after selecting the offer.',
  mobile_icon: 'Small icon for the offer in mobile view.',
  popup_icon: 'Icon displayed in the offer popup.',
  custom_name:
    'Custom name to display, eg. for a featured offer. Either set directly or define different languages as JSON (fallback always en)',
  trigger_login_reward:
    'For offers used as login rewards (e.g. the TGW tutorialLoginReward) this JSON config can schedule future login rewards. Link to the itemId of the next offer and the delay after which the user is supposed to get the next offer',

  // Hero-related
  hero_item_id: 'Checks whether the user owns the specified hero. If not, the offer is excluded.',
  level_item_id: "Represents the hero's level. The user must possess this item to show this offer.",
  medal_item_id: 'Used to determine how many medals the user currently holds for that hero.',
  required_medals:
    'Specifies the total number of medals required to upgrade to the next level—regardless of how many the user already owns. This ensures the offer always includes the full amount needed for an upgrade.',

  // Status & Metadata
  status: 'Current deployment status',
  sync_on: 'Current deployment status',
  ltv: "Lifetime value (based on user's ebpSum).",
} as const;

export type OfferFieldKey = keyof typeof OFFER_FIELD_TOOLTIPS;
